import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { accountApi } from '../../api/account_api';
import './ManageAccountsModal.css';

const ManageAccountsModal = ({ open, onClose, user }) => {
    console.log('ManageAccountsModal rendered with props - open:', open, 'user:', user);

    // Use ref to persistently store user data across re-renders
    const userRef = useRef(user);

    // Initialize localUser with user prop immediately to capture it before any re-renders
    const [localUser, setLocalUser] = useState(user);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAccountForm, setShowAccountForm] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);

    // Update both ref and state when user prop changes and is not null
    useEffect(() => {
        if (user) {
            console.log('Updating localUser and userRef from user prop:', user);
            userRef.current = user;
            setLocalUser(user);
        }
    }, [user]);

    // Reset localUser when modal closes
    useEffect(() => {
        if (!open) {
            console.log('Modal closed, resetting localUser');
            setLocalUser(null);
        }
    }, [open]);

    // Fetch accounts when modal opens
    useEffect(() => {
        if (open && localUser?.id) {
            fetchUserAccounts();
        }
    }, [open, localUser]);

    const fetchUserAccounts = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await accountApi.getAccountsByUserId(token, localUser.id);

            if (response.success) {
                setAccounts(response.data || []);
            } else {
                // Check if it's an authorization error
                if (response.error && response.error.includes('authentication')) {
                    toast.error('Authorization error: Admin permissions may be required. Please contact your system administrator.');
                } else {
                    toast.error(response.error || 'Failed to fetch user accounts');
                }
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            if (error.message && error.message.includes('403')) {
                toast.error('You do not have permission to view accounts. Admin access required.');
            } else {
                toast.error('Error loading accounts');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAccount = () => {
        setIsEditMode(false);
        setEditingAccount(null);
        formik.resetForm();
        setShowAccountForm(true);
    };

    const handleEditAccount = (account) => {
        console.log('Editing account:', account);

        setIsEditMode(true);
        setEditingAccount(account);

        // Extract the 6-digit suffix from the account number
        const accountNumberStr = account.accountNumber.toString();
        const suffix = accountNumberStr.slice(-6);

        // Ensure accountType is a number (0 or 1)
        const accountType = typeof account.accountType === 'number'
            ? account.accountType
            : (account.accountType === 'Demo' || account.accountType === 0 || account.accountType === '0') ? 0 : 1;

        console.log('Setting form values with accountType:', accountType);

        formik.setValues({
            accountType: accountType,
            accountNumber: suffix,
            currency: account.currency || 'USD'
        });

        setShowAccountForm(true);
    };

    const handleCloseAccountForm = () => {
        setShowAccountForm(false);
        setIsEditMode(false);
        setEditingAccount(null);
        formik.resetForm();
    };

    const handleDeleteClick = (account) => {
        setAccountToDelete(account);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!accountToDelete) return;

        try {
            const token = localStorage.getItem('token');
            const response = await accountApi.deleteAccount(token, accountToDelete.accountNumber);

            if (response.success) {
                toast.success('Account deleted successfully!');
                setDeleteDialogOpen(false);
                setAccountToDelete(null);
                fetchUserAccounts(); // Refresh the accounts list
            } else {
                toast.error(response.error || 'Failed to delete account');
            }
        } catch (error) {
            console.error('Error deleting account:', error);
            toast.error('Failed to delete account');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAccountToDelete(null);
    };

    // Formik form for creating/editing accounts
    const formik = useFormik({
        initialValues: {
            accountType: 0, // 0 = Demo, 1 = Live
            accountNumber: '',
            currency: 'USD'
        },
        validationSchema: Yup.object({
            accountType: Yup.number().required('Account type is required'),
            accountNumber: Yup.string()
                .matches(/^\d{6}$/, 'Account number must be exactly 6 digits')
                .required('Account number is required'),
            currency: Yup.string().required('Currency is required')
        }),
        onSubmit: async (values) => {
            try {
                console.log('Form submit - localUser:', localUser);
                console.log('Form submit - user prop:', user);
                console.log('Form submit - userRef.current:', userRef.current);

                // Validate user object - use multiple fallbacks: localUser -> user prop -> userRef
                const currentUser = localUser || user || userRef.current;

                if (!currentUser || !currentUser.id) {
                    console.error('User object is null or missing ID. localUser:', localUser, 'user prop:', user, 'userRef:', userRef.current);
                    toast.error('User information is missing. Please close and reopen the modal.');
                    return;
                }

                const token = localStorage.getItem('token');
                if (!token) {
                    toast.error('Authentication token not found');
                    return;
                }

                // Construct full account number with prefix
                const prefix = values.accountType === 1 ? '100' : '500';
                const fullAccountNumber = parseInt(prefix + values.accountNumber);

                if (isEditMode && editingAccount) {
                    // Update existing account
                    const accountData = {
                        userId: currentUser.id,
                        accountNumber: fullAccountNumber,
                        accountType: values.accountType,
                        currency: values.currency,
                        expiryDate: editingAccount.expiryDate || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString()
                    };

                    const response = await accountApi.updateAccount(token, editingAccount.accountNumber, accountData);

                    if (response.success) {
                        toast.success('Account updated successfully!');
                        setShowAccountForm(false);
                        setIsEditMode(false);
                        setEditingAccount(null);
                        formik.resetForm();
                        fetchUserAccounts(); // Refresh the accounts list
                    } else {
                        toast.error(response.error || 'Failed to update account');
                    }
                } else {
                    // Create new account
                    // Get initialDeposit from user data (if available), otherwise default to 0
                    const initialDeposit = currentUser.initialDeposit || 0;

                    const accountData = {
                        userId: currentUser.id,
                        accountNumber: fullAccountNumber,
                        accountType: values.accountType,
                        initialDeposit: initialDeposit,
                        currency: values.currency,
                        expiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString()
                    };

                    const response = await accountApi.createAccount(token, accountData);

                    if (response.success) {
                        toast.success('Account created successfully!');
                        setShowAccountForm(false);
                        formik.resetForm();
                        fetchUserAccounts(); // Refresh the accounts list
                    } else {
                        toast.error(response.error || 'Failed to create account');
                    }
                }
            } catch (error) {
                console.error('Error saving account:', error);
                toast.error(`Failed to ${isEditMode ? 'update' : 'create'} account`);
            }
        }
    });

    // Update account number prefix when account type changes
    useEffect(() => {
        if (formik.values.accountType === 1) {
            // Live account - show 100 prefix
            formik.setFieldValue('accountNumberPrefix', '100');
        } else {
            // Demo account - show 500 prefix
            formik.setFieldValue('accountNumberPrefix', '500');
        }
    }, [formik.values.accountType]);

    const getAccountTypeLabel = (type) => {
        return type === 1 ? 'Live' : 'Demo';
    };

    const getAccountTypeColor = (type) => {
        return type === 1 ? '#10b981' : '#f59e0b';
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 25px 50px 0 rgba(0, 0, 0, 0.15)',
                        backgroundColor: '#ffffff'
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 2,
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Manage Accounts
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                            {localUser?.firstName} {localUser?.lastName} ({localUser?.email})
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {/* Header with Add Account button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                            User Accounts ({accounts.length})
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateAccount}
                            sx={{
                                background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2,
                                py: 0.75,
                                borderRadius: 2,
                                boxShadow: '0 4px 14px 0 rgba(124, 58, 237, 0.35)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)'
                                }
                            }}
                        >
                            Add Account
                        </Button>
                    </Box>

                    {/* Accounts Table */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : accounts.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No accounts found for this user. Click "Add Account" to create one.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
                                        <TableCell sx={{
                                            fontWeight: 700,
                                            color: '#475569',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            py: 2.5,
                                            px: 3,
                                            borderBottom: '2px solid #e2e8f0'
                                        }}>
                                            Account Number
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 700,
                                            color: '#475569',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            py: 2.5,
                                            px: 3,
                                            borderBottom: '2px solid #e2e8f0'
                                        }}>
                                            Type
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 700,
                                            color: '#475569',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            py: 2.5,
                                            px: 3,
                                            borderBottom: '2px solid #e2e8f0'
                                        }}>
                                            Currency
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 700,
                                            color: '#475569',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            py: 2.5,
                                            px: 3,
                                            borderBottom: '2px solid #e2e8f0'
                                        }}>
                                            Balance
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 700,
                                            color: '#475569',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            py: 2.5,
                                            px: 3,
                                            borderBottom: '2px solid #e2e8f0'
                                        }}>
                                            Created
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 700,
                                            color: '#475569',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            textAlign: 'center',
                                            py: 2.5,
                                            px: 3,
                                            borderBottom: '2px solid #e2e8f0'
                                        }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {accounts.map((account) => (
                                        <TableRow
                                            key={account.accountId || account.accountNumber}
                                            hover
                                            sx={{
                                                backgroundColor: '#ffffff',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: '#f8fafc !important',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                                                },
                                                borderBottom: '1px solid #f1f5f9'
                                            }}
                                        >
                                            <TableCell sx={{ py: 2.5, px: 3, backgroundColor: 'transparent !important' }}>
                                                <Typography sx={{ fontFamily: 'Courier New, monospace', fontWeight: 600, color: '#1f2937' }}>
                                                    {account.accountNumber}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, px: 3, backgroundColor: 'transparent !important' }}>
                                                <Chip
                                                    label={getAccountTypeLabel(account.accountType)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getAccountTypeColor(account.accountType) + '20',
                                                        color: getAccountTypeColor(account.accountType),
                                                        fontWeight: 600,
                                                        fontSize: '0.8125rem',
                                                        height: 28,
                                                        borderRadius: '8px',
                                                        '& .MuiChip-label': {
                                                            px: 2
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, px: 3, backgroundColor: 'transparent !important' }}>
                                                <Typography sx={{ fontWeight: 500, color: '#6b7280', fontSize: '0.875rem' }}>
                                                    {account.currency}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, px: 3, backgroundColor: 'transparent !important' }}>
                                                <Typography sx={{ fontFamily: 'Courier New, monospace', fontWeight: 600, color: '#1f2937', fontSize: '0.9375rem' }}>
                                                    {account.balance || '0.00'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, px: 3, backgroundColor: 'transparent !important' }}>
                                                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', fontWeight: 500 }}>
                                                    {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', py: 2.5, px: 3, backgroundColor: 'transparent !important' }}>
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditAccount(account)}
                                                        sx={{
                                                            backgroundColor: '#e0e7ff',
                                                            color: '#7c3aed',
                                                            '&:hover': {
                                                                backgroundColor: '#c7d2fe',
                                                                transform: 'scale(1.05)'
                                                            },
                                                            transition: 'all 0.2s ease',
                                                            width: 32,
                                                            height: 32
                                                        }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteClick(account)}
                                                        sx={{
                                                            backgroundColor: '#fee2e2',
                                                            color: '#dc2626',
                                                            '&:hover': {
                                                                backgroundColor: '#fecaca',
                                                                transform: 'scale(1.05)'
                                                            },
                                                            transition: 'all 0.2s ease',
                                                            width: 32,
                                                            height: 32
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
                    <Button
                        onClick={onClose}
                        sx={{
                            color: '#6b7280',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#f3f4f6'
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Account Form Dialog */}
            <Dialog
                open={showAccountForm}
                onClose={handleCloseAccountForm}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 25px 50px 0 rgba(0, 0, 0, 0.15)',
                        backgroundColor: '#ffffff',
                        color: '#1f2937'
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 2,
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#1f2937'
                }}>
                    <Box>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600, color: '#1f2937', fontSize: '1.5rem', lineHeight: 1.2 }}>
                            {isEditMode ? 'Edit Account' : 'Add New Account'}
                        </Box>
                        <Box component="span" sx={{ display: 'block', color: '#6b7280', mt: 1, fontSize: '0.875rem', lineHeight: 1.5 }}>
                            {isEditMode ? 'Update' : 'Create a new'} trading account for {localUser?.firstName} {localUser?.lastName}
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
                    <form onSubmit={formik.handleSubmit}>
                        {/* Account Type */}
                        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                            <InputLabel
                                id="account-type-label"
                                sx={{
                                    color: '#6b7280',
                                    backgroundColor: '#ffffff',
                                    px: 1,
                                    '&.Mui-focused': {
                                        color: '#7c3aed'
                                    }
                                }}
                            >
                                Account Type
                            </InputLabel>
                            <Select
                                labelId="account-type-label"
                                id="account-type-select"
                                name="accountType"
                                value={formik.values.accountType}
                                onChange={(e) => {
                                    formik.handleChange(e);
                                    // Clear account number when type changes
                                    formik.setFieldValue('accountNumber', '');
                                }}
                                label="Account Type"
                                sx={{
                                    color: '#1f2937',
                                    backgroundColor: '#ffffff !important',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#d1d5db !important'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#9ca3af !important'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#7c3aed !important'
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: '#ffffff !important',
                                        padding: '16.5px 14px'
                                    }
                                }}
                            >
                                <MenuItem value={0}>Demo Account</MenuItem>
                                <MenuItem value={1}>Live Account</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Account Number with Prefix Display */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ color: '#374151', mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
                                Account Number
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                {/* Prefix Display */}
                                <Box sx={{
                                    px: 3,
                                    py: 2,
                                    backgroundColor: '#f9fafb',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    minWidth: 80,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                                }}>
                                    <Typography sx={{
                                        fontFamily: 'Courier New, monospace',
                                        fontWeight: 700,
                                        color: '#1f2937',
                                        fontSize: '1.125rem',
                                        letterSpacing: '0.05em'
                                    }}>
                                        {formik.values.accountType === 1 ? '100' : '500'}
                                    </Typography>
                                </Box>

                                {/* 6-digit Input */}
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        fullWidth
                                        name="accountNumber"
                                        placeholder="Enter 6 digits"
                                        value={formik.values.accountNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.accountNumber && Boolean(formik.errors.accountNumber)}
                                        helperText={formik.touched.accountNumber && formik.errors.accountNumber}
                                        className="account-number-input"
                                        InputProps={{
                                            style: {
                                                fontFamily: 'Courier New, monospace',
                                                fontSize: '1.125rem',
                                                fontWeight: 600,
                                                letterSpacing: '0.05em',
                                                padding: '0'
                                            }
                                        }}
                                        inputProps={{
                                            maxLength: 6,
                                            style: {
                                                fontFamily: 'Courier New, monospace',
                                                fontSize: '1.125rem',
                                                fontWeight: 600,
                                                letterSpacing: '0.05em',
                                                padding: '14px 16px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: '#1f2937 !important',
                                                backgroundColor: '#ffffff !important',
                                                borderRadius: '8px !important',
                                                padding: '0 !important',
                                                '& fieldset': {
                                                    borderColor: '#d1d5db !important',
                                                    borderWidth: '2px !important'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#9ca3af !important'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#7c3aed !important',
                                                    borderWidth: '2px !important'
                                                }
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '14px 16px !important',
                                                fontFamily: 'Courier New, monospace !important',
                                                fontSize: '1.125rem !important',
                                                fontWeight: '600 !important',
                                                letterSpacing: '0.05em !important',
                                                color: '#1f2937 !important'
                                            },
                                            '& .MuiFormHelperText-root': {
                                                marginLeft: '0 !important',
                                                marginTop: '6px !important',
                                                fontSize: '0.8125rem !important'
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '6px'
                            }}>
                                <Typography variant="caption" sx={{ color: '#166534', fontSize: '0.8125rem', fontWeight: 500 }}>
                                    Full Account Number: <strong style={{
                                        fontFamily: 'Courier New, monospace',
                                        fontSize: '0.9375rem',
                                        letterSpacing: '0.05em'
                                    }}>
                                        {formik.values.accountNumber ?
                                            (formik.values.accountType === 1 ? '100' : '500') + formik.values.accountNumber :
                                            (formik.values.accountType === 1 ? '100' : '500') + 'XXXXXX'}
                                    </strong>
                                </Typography>
                            </Box>
                        </Box>

                        {/* Currency */}
                        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                            <InputLabel
                                id="currency-label"
                                sx={{
                                    color: '#6b7280',
                                    backgroundColor: '#ffffff',
                                    px: 1,
                                    '&.Mui-focused': {
                                        color: '#7c3aed'
                                    }
                                }}
                            >
                                Currency
                            </InputLabel>
                            <Select
                                labelId="currency-label"
                                id="currency-select"
                                name="currency"
                                value={formik.values.currency}
                                onChange={formik.handleChange}
                                label="Currency"
                                sx={{
                                    color: '#1f2937',
                                    backgroundColor: '#ffffff !important',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#d1d5db !important'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#9ca3af !important'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#7c3aed !important'
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: '#ffffff !important',
                                        padding: '16.5px 14px'
                                    }
                                }}
                            >
                                <MenuItem value="USD">USD - US Dollar</MenuItem>
                                <MenuItem value="EUR">EUR - Euro</MenuItem>
                                <MenuItem value="GBP">GBP - British Pound</MenuItem>
                                <MenuItem value="JPY">JPY - Japanese Yen</MenuItem>
                                <MenuItem value="AUD">AUD - Australian Dollar</MenuItem>
                                <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
                                <MenuItem value="CHF">CHF - Swiss Franc</MenuItem>
                            </Select>
                        </FormControl>
                    </form>
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                    <Button
                        onClick={handleCloseAccountForm}
                        sx={{
                            color: '#6b7280',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            '&:hover': {
                                backgroundColor: '#f3f4f6'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={formik.handleSubmit}
                        disabled={formik.isSubmitting || !formik.isValid}
                        sx={{
                            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                            color: 'white',
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            boxShadow: '0 4px 14px 0 rgba(124, 58, 237, 0.35)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)',
                                boxShadow: '0 6px 20px 0 rgba(124, 58, 237, 0.45)'
                            },
                            '&:disabled': {
                                background: '#e5e7eb',
                                color: '#9ca3af'
                            }
                        }}
                    >
                        {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : isEditMode ? 'Update Account' : 'Create Account'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 25px 50px 0 rgba(0, 0, 0, 0.15)'
                    }
                }}
            >
                <DialogTitle sx={{ pb: 2, fontWeight: 600, color: '#1f2937' }}>
                    Delete Account
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#6b7280', mb: 2 }}>
                        Are you sure you want to delete this account?
                    </Typography>
                    {accountToDelete && (
                        <Box sx={{
                            p: 2,
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: 2
                        }}>
                            <Typography sx={{ fontWeight: 600, color: '#991b1b', mb: 1 }}>
                                Account Details:
                            </Typography>
                            <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                <strong>Account Number:</strong> {accountToDelete.accountNumber}
                            </Typography>
                            <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                <strong>Type:</strong> {getAccountTypeLabel(accountToDelete.accountType)}
                            </Typography>
                            <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                <strong>Currency:</strong> {accountToDelete.currency}
                            </Typography>
                        </Box>
                    )}
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                        This action cannot be undone. All account data will be permanently deleted.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
                    <Button
                        onClick={handleDeleteCancel}
                        sx={{
                            color: '#6b7280',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#f3f4f6'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        sx={{
                            backgroundColor: '#dc2626',
                            color: 'white',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#b91c1c'
                            }
                        }}
                    >
                        Delete Account
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ManageAccountsModal;
